import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { InstanaLoggingBootstrapperConfig } from '../bootstrappers/instana-logging-bootstrapper.config';
import { DEFAULT_SCRIPT_LOAD_TIMEOUT_MS } from '../constants/script-load-timeout.constant';

/**
 * Service responsible for loading the Instana EUM script with timeout and error handling.
 *
 * This service loads scripts directly using native DOM APIs to avoid dependencies on
 * resource loaders that may not be registered yet during early initialization.
 *
 * Features:
 * - Timeout management
 * - Promise-based error handling (rejects on failure)
 * - Script ID tracking for duplicate detection
 * - Direct DOM manipulation (no dependency on DomResourceService)
 *
 * Architecture Note: Uses direct DOM APIs instead of DomResourceService because
 * Instana early initialization runs before JsResourceLoaderInitializer registers
 * the JavaScript resource loader.
 */
@Injectable()
export class InstanaScriptLoaderService {
  public constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
  ) { }

  /**
   * Loads the Instana EUM script from the configured URL.
   *
   * @param config Instana configuration containing script URL and timeout
   * @param scriptId Unique identifier for the script element
   * @param onSuccess Optional callback invoked when script loads successfully
   * @param onFailure Optional callback invoked when script fails to load or times out
   * @returns Promise that resolves on success or rejects on failure
   */
  public loadScript(
    config: InstanaLoggingBootstrapperConfig,
    scriptId: string,
    onSuccess?: () => void,
    onFailure?: () => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (this._document?.getElementById(scriptId)) {
        onSuccess?.();
        resolve();
        return;
      }

      // Race condition prevention: Use resolveOnce/rejectOnce pattern to ensure
      // the promise is resolved or rejected exactly once, even if multiple events fire.
      // This prevents "Promise already resolved" errors that can occur when:
      // - Script loads successfully AND timeout fires (race condition)
      // - Script error event AND timeout fire simultaneously
      // - Multiple error handlers trigger (shouldn't happen, but defensive)
      let resolved = false;
      const resolveOnce = () => {
        if (!resolved) {
          resolved = true;
          resolve();
        }
      };
      const rejectOnce = (error: Error) => {
        if (!resolved) {
          resolved = true;
          reject(error);
        }
      };

      // Timeout fallback: if script takes longer than configured timeout, trigger failure
      // This ensures the promise doesn't hang indefinitely if the script fails to load
      const timeoutMs = config.scriptLoadTimeout ?? DEFAULT_SCRIPT_LOAD_TIMEOUT_MS;
      const timeoutId = setTimeout(() => {
        if (!resolved) {
          const error = new Error(
            `Instana EUM script failed to load or timed out (${timeoutMs}ms) from ${config.scriptUrl}`
          );
          onFailure?.();
          rejectOnce(error);
        }
      }, timeoutMs);

      // Load script directly using native DOM APIs instead of DomResourceService.
      // This is necessary because Instana early initialization runs before
      // JsResourceLoaderInitializer registers the JavaScript resource loader.
      // Using native APIs ensures the script can load regardless of resource loader registration.
      try {
        const script = this._document.createElement('script');
        script.id = scriptId;
        script.src = config.scriptUrl;
        script.type = 'text/javascript';
        script.async = true; // Load asynchronously to avoid blocking page rendering

        // Success handler: script loaded successfully
        script.addEventListener('load', () => {
          clearTimeout(timeoutId); // Cancel timeout since script loaded
          onSuccess?.();
          resolveOnce(); // Resolve promise exactly once
        });

        // Error handler: script failed to load (network error, 404, etc.)
        script.addEventListener('error', (_event) => {
          clearTimeout(timeoutId); // Cancel timeout
          const loadError = new Error(
            `Failed to load Instana script from ${config.scriptUrl}`
          );
          onFailure?.();
          rejectOnce(loadError); // Reject promise exactly once
        });

        // Append script to document head
        // Fallback to getElementsByTagName for older browsers (though unlikely needed)
        const head = this._document.head || this._document.getElementsByTagName('head')[0];
        if (head) {
          head.appendChild(script);
        } else {
          // Edge case: document head not available (shouldn't happen in normal browsers)
          clearTimeout(timeoutId);
          const error = new Error('Document head not available for script injection');
          onFailure?.();
          rejectOnce(error);
        }
      } catch (error) {
        // Catch any synchronous errors during script element creation
        // (e.g., invalid URL, DOM manipulation errors)
        clearTimeout(timeoutId);
        const loadError = error instanceof Error
          ? error
          : new Error(`Failed to create script element for ${config.scriptUrl}: ${String(error)}`);
        onFailure?.();
        rejectOnce(loadError);
      }
    });
  }

}
